import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  Popover,
  ThemeProvider,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import useStorefrontCurrency from "@features/ecommerce/hooks/useStorefrontCurrency";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import { getShiComBaseUrl } from "@lib/shiComBaseUrl";
import { getSaveAsQuoteUrl } from "@features/ecommerce/cart/saveAsQuoteUrl";
import { getCheckoutEntryPath } from "@features/ecommerce/cart/checkoutEntryPath";
import { AppendIfPrefixExists } from "modules/sitePaths";
import { useFeatureFlag } from "shared-ui/src/utils/FeatureFlagHelper";
import { COMMODITY_PRODUCT_CLASS } from "shared-ui/src/types/ecommerce/constants";
import type StandardRow from "shared-ui/src/types/ecommerce/StandardRow";
import type { ShoppingCartDto } from "@lib/ShiOneClient";
import { useThemeMode } from "../../../../../theme/ThemeModeProviderContext";
import lightTheme from "shared-ui/src/theme";
import darkTheme from "shared-ui/src/theme/darkTheme";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../auth/authorizationTypes";
import useCart from "@features/ecommerce/cart/useCart";
import useAddShoppingCartProduct from "@features/ecommerce/hooks/useAddShoppingCartProduct";
import useGetCartProductThumbnails from "@features/unified-user-header-feature/hooks/useGetCartProductThumbnails";
import SaveQuoteDialog from "../../../pages/quotes/SaveQuoteDialog";
import { StandardsShopProductDetailDrawer } from "../../../pages/ecommerce/standards-shop/product-detail-drawer/StandardsShopProductDetailDrawer";

const baseUrl = getShiComBaseUrl();

/**
 * Adapts a cart line to the StandardRow shape the Standards Shop quick view consumes so the
 * mini-cart reuses that drawer instead of navigating out to the SHI.com PDP. The cart API's
 * `itemId` is the catalog product descriptor (same id the `/product/{id}` link and the
 * thumbnail lookup use). `ShoppingCartDto` carries no product class, so lines are treated as
 * commodity; config-system lines fall back to the drawer's basic (unenriched) display.
 */
function cartLineToStandardRow(
  item: ShoppingCartDto,
  index: number,
  thumbnail: string | undefined,
): StandardRow {
  return {
    id: index,
    stringId: `cart-line-${item.lineNumber ?? item.itemId ?? index}`,
    name: item.productName ?? "",
    isGroup: false,
    productDescriptor: item.itemId ?? null,
    productClass: COMMODITY_PRODUCT_CLASS,
    price: item.unitPrice,
    imageLocation: thumbnail ?? null,
    canAddToCart: item.itemId != null,
  };
}

export interface MiniCartDialogProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

/**
 * Mini-cart dropdown for the redesigned cart experience (SPU-532). Per the epic spec this is
 * intentionally a lightweight overview: display-only quantities, subtotal only (no
 * tax/shipping/total), no expandable details. It shares the cart cache via `useCart` so it
 * stays in sync with the full cart page. Actions: Checkout (primary), Save as Quote (header,
 * when permitted), and View Cart (navigates to the in-app full cart page). Clicking a product
 * name opens the shared Standards Shop quick view drawer rather than leaving for the SHI.com PDP.
 */
export default function MiniCartDialog({
  open,
  anchorEl,
  onClose,
}: MiniCartDialogProps) {
  const { formatAmount } = useStorefrontCurrency();
  const navigate = useNavigate();
  // UnifiedHeader wraps the header (and everything it portals) in unifiedHeaderTheme,
  // which repurposes background.paper as the header surface grey. Re-theme with the app
  // palette so background tokens resolve normally — same pattern as the profile drawers.
  const { mode } = useThemeMode();
  const dialogTheme = mode === "light" ? lightTheme : darkTheme;
  const { hasPermission } = useClaims();
  const canSaveAsQuote = hasPermission(
    ShiOnePermission.shoppingCartSaveAsQuote,
  );
  // When on, "Save as Quote" opens the native cart-fed Save Quote dialog (SPU-493/494)
  // instead of redirecting to the SPU-538 SHI.com save-as-quote flow.
  const { enabled: createQuotesEnabled } = useFeatureFlag(
    "spu.create-quotes",
    false,
  );
  const saveAsQuoteOpensDialog = createQuotesEnabled;
  // When on, "Checkout" enters the in-app checkout flow (/checkout) instead of
  // redirecting to the SHI.com order-request page. Treat undefined (still loading)
  // as off so we never flash the new flow before the flag resolves.
  const { enabled: checkoutMvpEnabled } = useFeatureFlag(
    "synergists.enable-checkout-mvp",
    false,
  );

  const [saveQuoteOpen, setSaveQuoteOpen] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState<StandardRow | null>(
    null,
  );

  const { items, itemCount, subtotal, removeItem } = useCart();
  const isEmpty = items.length === 0;

  const itemIds = useMemo(
    () =>
      items.map((item) => item.itemId).filter((id): id is number => id != null),
    [items],
  );
  const { data: thumbnails } = useGetCartProductThumbnails(itemIds, open);

  const addToCart = useAddShoppingCartProduct();

  // The quick view is a right-anchored drawer that would sit underneath this popover, and
  // stacking two MUI modals fights over focus/outside-click. Close the mini-cart first.
  const openQuickView = (product: StandardRow) => {
    onClose();
    setQuickViewProduct(product);
  };

  const goToCart = () => {
    onClose();
    navigate(AppendIfPrefixExists("/cart"));
  };
  const viewCartNavigationProps = { onClick: goToCart };

  return (
    <ThemeProvider theme={dialogTheme}>
      <Popover
        open={open && Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: 360,
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
              borderRadius: 2,
              mt: 0.5,
            },
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Cart Preview
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {!isEmpty && canSaveAsQuote && (
              <Button
                variant="text"
                color="primary"
                size="small"
                onClick={() => {
                  if (saveAsQuoteOpensDialog) {
                    setSaveQuoteOpen(true);
                  }
                  onClose();
                }}
                {...(saveAsQuoteOpensDialog
                  ? {}
                  : { href: getSaveAsQuoteUrl() })}
                {...telemetryProps({
                  name: "cart_preview_save_as_quote",
                  section: "cart_preview",
                  label: "Save as Quote",
                })}
                sx={{ fontSize: 13 }}
              >
                Save as Quote
              </Button>
            )}
            <IconButton
              size="small"
              aria-label="Close cart"
              onClick={onClose}
              {...telemetryProps({
                name: "cart_preview_close",
                section: "cart_preview",
                label: "Close Cart",
              })}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Divider />

        {isEmpty ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              px: 3,
              py: 5,
              bgcolor: "background.paper",
              gap: 1.5,
            }}
          >
            <ShoppingCartOutlinedIcon
              sx={{ fontSize: 48, color: "text.disabled" }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={500}
              textAlign="center"
            >
              Your cart is empty
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              textAlign="center"
            >
              Explore products or browse your standards to add products.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="small"
              fullWidth
              {...viewCartNavigationProps}
              {...telemetryProps({
                name: "cart_preview_view_cart",
                section: "cart_preview",
                label: "View Cart",
              })}
            >
              View Cart
            </Button>
          </Box>
        ) : (
          <>
            <Box
              sx={{ flex: 1, overflowY: "auto", bgcolor: "background.paper" }}
            >
              <List disablePadding>
                {items.map((item, index) => {
                  const thumb =
                    item.itemId != null
                      ? thumbnails?.[String(item.itemId)]
                      : undefined;
                  // lineNumber/itemId are both optional on the DTO; fall back to the array index
                  // so rows never collide on an `undefined-undefined` key.
                  const rowKey =
                    item.lineNumber ?? item.itemId ?? `idx-${index}`;
                  const lineNumber = item.lineNumber ?? 0;
                  const quantity = item.quantity ?? 0;
                  return (
                    <ListItem
                      key={rowKey}
                      disablePadding
                      sx={{
                        px: 2,
                        py: 1.25,
                        alignItems: "flex-start",
                        ...(index > 0 && {
                          borderTop: "1px solid",
                          borderColor: "divider",
                        }),
                      }}
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          flexShrink: 0,
                          mr: 1.5,
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        {thumb ? (
                          <Box
                            component="img"
                            src={thumb}
                            alt={item.productName ?? "Product"}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <ImageOutlinedIcon
                            sx={{ fontSize: 20, color: "text.disabled" }}
                          />
                        )}
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                        {item.itemId != null ? (
                          <Link
                            component="button"
                            type="button"
                            onClick={() =>
                              openQuickView(
                                cartLineToStandardRow(item, index, thumb),
                              )
                            }
                            underline="hover"
                            color="text.primary"
                            variant="body2"
                            {...telemetryProps({
                              name: "cart_preview_quick_view",
                              section: "cart_preview",
                              label: "Open Quick View",
                              id: item.itemId.toString(),
                            })}
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              mb: 0.5,
                              textAlign: "left",
                              cursor: "pointer",
                              fontWeight: 400,
                              userSelect: "text",
                              WebkitUserSelect: "text",
                            }}
                          >
                            {item.productName}
                          </Link>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              mb: 0.5,
                            }}
                          >
                            {item.productName}
                          </Typography>
                        )}
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Qty: {quantity}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            ·
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight={500}
                            sx={{ color: "warning.dark" }}
                          >
                            {formatAmount(
                              Number(item.unitPrice ?? 0) * quantity,
                            )}
                          </Typography>
                        </Box>
                      </Box>

                      <Tooltip title="Remove from cart">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Remove ${item.productName ?? "item"}`}
                            disabled={lineNumber <= 0}
                            onClick={() => {
                              if (lineNumber > 0) {
                                removeItem(lineNumber);
                              }
                            }}
                            {...telemetryProps({
                              name: "cart_preview_remove_item",
                              section: "cart_preview",
                              label: "Remove item",
                              id: item.itemId?.toString(),
                            })}
                            sx={{
                              color: "text.disabled",
                              mt: 0.25,
                              "&:hover": { color: "error.main" },
                            }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </ListItem>
                  );
                })}
              </List>
            </Box>

            <Divider />

            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                bgcolor: "background.default",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Subtotal
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ({itemCount} {itemCount === 1 ? "item" : "items"})
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight={600}>
                {formatAmount(subtotal)}
              </Typography>
            </Box>
          </>
        )}

        <Divider />

        {!isEmpty && (
          <Box
            sx={{
              px: 2.5,
              py: 2,
              bgcolor: "background.paper",
              display: "flex",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Button
              variant="outlined"
              {...viewCartNavigationProps}
              {...telemetryProps({
                name: "cart_preview_view_cart",
                section: "cart_preview",
                label: "View Cart",
              })}
              fullWidth
            >
              View Cart
            </Button>
            <Button
              variant="contained"
              {...(checkoutMvpEnabled
                ? {}
                : { href: `${baseUrl}/CheckOut/OrderRequest.aspx` })}
              onClick={() => {
                onClose();
                if (checkoutMvpEnabled) {
                  navigate(getCheckoutEntryPath());
                }
              }}
              {...telemetryProps({
                name: "cart_preview_checkout",
                section: "cart_preview",
                label: "Checkout",
              })}
              fullWidth
            >
              Checkout
            </Button>
          </Box>
        )}
      </Popover>
      <StandardsShopProductDetailDrawer
        open={quickViewProduct !== null}
        product={quickViewProduct}
        onClose={() => setQuickViewProduct(null)}
        onAddToCart={(quantity) => {
          if (quickViewProduct?.productDescriptor == null) return;
          addToCart.mutate({
            id: quickViewProduct.stringId,
            productDescriptor: quickViewProduct.productDescriptor.toString(),
            productClass: quickViewProduct.productClass ?? undefined,
            name: quickViewProduct.name,
            price: quickViewProduct.price,
            imageUrl: quickViewProduct.imageLocation ?? undefined,
            quantity,
          });
        }}
      />
      {saveAsQuoteOpensDialog && (
        <SaveQuoteDialog
          open={saveQuoteOpen}
          onClose={() => setSaveQuoteOpen(false)}
          cartItems={items}
        />
      )}
    </ThemeProvider>
  );
}
